import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingContactButtons } from "@/components/FloatingContactButtons";
import { MobileStickyCTA } from "@/components/MobileStickyCTA";
import { Toaster } from "@/components/ui/sonner";
import { ScrollToTop } from "@/hooks/useAnimations";
import { SeoManager } from "@/components/SeoManager";
import { ConsentBanner } from "@/components/ConsentBanner";
import { AnalyticsManager } from "@/components/AnalyticsManager";
import { trackContactConversion } from "@/utils/leads";

const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const AdsLanding = lazy(() => import("@/pages/AdsLanding"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const VirtualOffice = lazy(() => import("@/pages/VirtualOffice"));
const BusinessSetup = lazy(() => import("@/pages/BusinessSetup"));
const PrivateLimitedRegistration = lazy(() =>
  import("@/pages/PrivateLimitedRegistration"),
);
const RegistrationJourney = lazy(() => import("@/pages/RegistrationJourney"));
const ComplianceServices = lazy(() => import("@/pages/ComplianceServices"));
const AllPrices = lazy(() => import("@/pages/AllPrices"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const BusinessMembership = lazy(() => import("@/pages/BusinessMembership"));
const BusinessTools = lazy(() => import("@/pages/BusinessTools"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const TrustCentrePage = lazy(() => import("@/pages/TrustCentrePage"));
const CampaignLanding = lazy(() => import("@/pages/CampaignLanding"));
const PanIndiaVirtualOfficeAds = lazy(() => import("@/pages/PanIndiaVirtualOfficeAds"));

const PageLoader = () => (
  <div className="flex min-h-[70vh] items-center justify-center bg-white pt-24" role="status" aria-live="polite">
    <div className="flex items-center gap-3 rounded-full border border-emerald-100 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-800 shadow-sm">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
      Loading BizSarthi service...
    </div>
  </div>
);

const StandardHeader = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/ads/")) return null;
  return (
    <>
      <Navbar />
      <FloatingContactButtons />
    </>
  );
};

const StandardFooter = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/ads/")) return null;
  return (
    <>
      <Footer />
      <MobileStickyCTA />
    </>
  );
};

function App() {
  useEffect(() => {
    const trackContactLink = (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link) return;
      // Ads landing pages track their contact CTAs with richer page/location data.
      // Skipping the global listener there prevents double-counted conversions.
      if (window.location.pathname.startsWith("/ads/")) return;
      const href = link.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        trackContactConversion("call", { page_source: window.location.pathname });
      } else if (href.includes("wa.me/")) {
        trackContactConversion("whatsapp", {
          page_source: window.location.pathname,
        });
      }
    };

    document.addEventListener("click", trackContactLink);
    return () => document.removeEventListener("click", trackContactLink);
  }, []);

  const previewBase =
    window.location.pathname.match(/^\/proxy\/\d+/)?.[0] || "";

  return (
    <div className="App">
      <input
        id="bizsarthi-website-field"
        name="website"
        type="text"
        autoComplete="off"
        tabIndex="-1"
        aria-hidden="true"
        className="fixed -left-[9999px] top-0 h-px w-px opacity-0"
      />
      <BrowserRouter basename={previewBase}>
        <ScrollToTop />
        <SeoManager />
        <AnalyticsManager />
        <StandardHeader />
        <div className="min-h-screen">
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/virtual-office" element={<VirtualOffice />} />
            <Route path="/virtual-office/:locationSlug" element={<VirtualOffice />} />
            <Route path="/business-setup" element={<BusinessSetup />} />
            <Route path="/private-limited-company-registration" element={<PrivateLimitedRegistration />} />
            <Route path="/private-limited-company-registration/:stateSlug" element={<PrivateLimitedRegistration />} />
            <Route path="/llp-registration" element={<RegistrationJourney serviceKey="llp" />} />
            <Route path="/llp-registration/:stateSlug" element={<RegistrationJourney serviceKey="llp" />} />
            <Route path="/trademark-registration" element={<RegistrationJourney serviceKey="trademark" />} />
            <Route path="/proprietorship-registration" element={<RegistrationJourney serviceKey="proprietorship" />} />
            <Route path="/proprietorship-registration/:stateSlug" element={<RegistrationJourney serviceKey="proprietorship" />} />
            <Route path="/partnership-registration" element={<RegistrationJourney serviceKey="partnership" />} />
            <Route path="/partnership-registration/:stateSlug" element={<RegistrationJourney serviceKey="partnership" />} />
            <Route path="/startup-india-registration" element={<RegistrationJourney serviceKey="startup-india" />} />
            <Route path="/registration/:serviceSlug" element={<RegistrationJourney />} />
            <Route path="/registration/:serviceSlug/:stateSlug" element={<RegistrationJourney />} />
            <Route path="/compliance-services" element={<ComplianceServices />} />
            <Route path="/all-prices" element={<AllPrices />} />
            <Route path="/business-membership" element={<BusinessMembership />} />
            <Route path="/business-tools" element={<BusinessTools />} />
            <Route path="/tools" element={<BusinessTools />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:articleSlug" element={<BlogArticle />} />
            <Route path="/trust-centre" element={<TrustCentrePage />} />
            <Route path="/ads/virtual-office-haryana" element={<CampaignLanding campaignKey="virtual-office-haryana" />} />
            <Route path="/ads/gurugram-virtual-office" element={<CampaignLanding campaignKey="virtual-office-haryana" />} />
            <Route path="/ads/virtual-office-india" element={<PanIndiaVirtualOfficeAds />} />
            <Route path="/ads/private-limited-registration" element={<CampaignLanding campaignKey="private-limited-registration" />} />
            <Route path="/ads/gst-registration" element={<CampaignLanding campaignKey="gst-registration" />} />
            <Route path="/ads/trademark-registration" element={<CampaignLanding campaignKey="trademark-registration" />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/get-started" element={<AdsLanding />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
        <StandardFooter />
        <ConsentBanner />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
